import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface SalaryManagementProps {
  domain: string;
}

interface Teacher {
  id: string;
  name: string;
  employeeId: string;
  email: string;
  phone: string;
  department?: string;
  designation?: string;
  joiningDate?: string;
  salaryStructure?: SalaryStructure;
}

interface SalaryStructure {
  basicSalary: number;
  houseRentAllowance: number;
  dearnessAllowance: number;
  medicalAllowance: number;
  transportAllowance: number;
  specialAllowance: number;
  providentFund: number;
  professionalTax: number;
  incomeTax: number;
  otherDeductions: number;
  effectiveFrom: string;
  updatedAt: string;
}

interface SalaryPayment {
  id: string;
  teacherId: string;
  teacherName: string;
  employeeId: string;
  month: string;
  year: string;
  basicSalary: number;
  totalEarnings: number;
  totalDeductions: number;
  netSalary: number;
  paymentDate: string;
  paymentMode: string;
  transactionId?: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  status: 'pending' | 'paid' | 'cancelled' | 'processing';
  remarks?: string;
  createdAt: string;
  paidAt?: string;
}

const SalaryManagement: React.FC<SalaryManagementProps> = ({ domain }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'assign' | 'payments'>('assign');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [processing, setProcessing] = useState(false);

  // Salary Assignment State
  const [salaryForm, setSalaryForm] = useState<SalaryStructure>({
    basicSalary: 0,
    houseRentAllowance: 0,
    dearnessAllowance: 0,
    medicalAllowance: 0,
    transportAllowance: 0,
    specialAllowance: 0,
    providentFund: 0,
    professionalTax: 0,
    incomeTax: 0,
    otherDeductions: 0,
    effectiveFrom: new Date().toISOString().split('T')[0],
    updatedAt: new Date().toISOString(),
  });

  // Payment State
  const [payments, setPayments] = useState<SalaryPayment[]>([]);
  const [paymentForm, setPaymentForm] = useState({
    teacherId: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString(),
    paymentMode: 'Bank Transfer',
    transactionId: '',
    remarks: '',
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - 2 + i).toString());

  useEffect(() => {
    loadTeachers();
    loadPayments();
    loadRazorpayScript();
  }, [domain]);

  useEffect(() => {
    if (selectedTeacher) {
      const teacher = teachers.find(t => t.id === selectedTeacher);
      if (teacher?.salaryStructure) {
        setSalaryForm(teacher.salaryStructure);
      } else {
        // Reset to default
        setSalaryForm({
          basicSalary: 0,
          houseRentAllowance: 0,
          dearnessAllowance: 0,
          medicalAllowance: 0,
          transportAllowance: 0,
          specialAllowance: 0,
          providentFund: 0,
          professionalTax: 0,
          incomeTax: 0,
          otherDeductions: 0,
          effectiveFrom: new Date().toISOString().split('T')[0],
          updatedAt: new Date().toISOString(),
        });
      }
    }
  }, [selectedTeacher, teachers]);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const snapshot = await getDocs(teachersRef);

      const teachersList: Teacher[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Teacher));

      setTeachers(teachersList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error: any) {
      console.error('Error loading teachers:', error);
      alert(`Failed to load teachers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const paymentsRef = collection(db, `school-data/${domain}/salary-payments`);
      const snapshot = await getDocs(paymentsRef);

      const paymentsList: SalaryPayment[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          teacherId: data.teacherId || '',
          teacherName: data.teacherName || 'Unknown',
          employeeId: data.employeeId || '',
          month: data.month || '',
          year: data.year || '',
          basicSalary: data.basicSalary || 0,
          totalEarnings: data.totalEarnings || 0,
          totalDeductions: data.totalDeductions || 0,
          netSalary: data.netSalary || 0,
          paymentDate: data.paymentDate || '',
          paymentMode: data.paymentMode || '',
          transactionId: data.transactionId,
          razorpayOrderId: data.razorpayOrderId,
          razorpayPaymentId: data.razorpayPaymentId,
          razorpaySignature: data.razorpaySignature,
          status: data.status || 'pending',
          remarks: data.remarks,
          createdAt: data.createdAt || new Date().toISOString(),
          paidAt: data.paidAt,
        } as SalaryPayment;
      });

      setPayments(paymentsList.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (error: any) {
      console.error('Error loading payments:', error);
      if (error.code !== 'permission-denied') {
        alert(`Failed to load payments: ${error.message}`);
      }
      setPayments([]);
    }
  };

  const calculateTotalEarnings = (): number => {
    return (
      salaryForm.basicSalary +
      salaryForm.houseRentAllowance +
      salaryForm.dearnessAllowance +
      salaryForm.medicalAllowance +
      salaryForm.transportAllowance +
      salaryForm.specialAllowance
    );
  };

  const calculateTotalDeductions = (): number => {
    return (
      salaryForm.providentFund +
      salaryForm.professionalTax +
      salaryForm.incomeTax +
      salaryForm.otherDeductions
    );
  };

  const calculateNetSalary = (): number => {
    return calculateTotalEarnings() - calculateTotalDeductions();
  };

  const handleSalaryFormChange = (field: keyof SalaryStructure, value: any) => {
    setSalaryForm({ ...salaryForm, [field]: value });
  };

  const handleAssignSalary = async () => {
    if (!selectedTeacher) {
      alert('Please select a teacher!');
      return;
    }

    if (salaryForm.basicSalary <= 0) {
      alert('Basic salary must be greater than 0!');
      return;
    }

    if (!confirm('Are you sure you want to assign/update this salary structure?')) {
      return;
    }

    setProcessing(true);
    try {
      const teacher = teachers.find(t => t.id === selectedTeacher);
      if (!teacher) throw new Error('Teacher not found');

      const teacherRef = doc(db, `school-users/${domain}/teachers`, selectedTeacher);

      const updatedSalaryStructure = {
        ...salaryForm,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(teacherRef, {
        salaryStructure: updatedSalaryStructure,
        updatedAt: new Date().toISOString(),
      });

      alert(`Salary structure assigned successfully to ${teacher.name}!`);
      loadTeachers();
    } catch (error: any) {
      console.error('Error assigning salary:', error);
      alert(`Failed to assign salary: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleGeneratePayment = async () => {
    if (!paymentForm.teacherId) {
      alert('Please select a teacher!');
      return;
    }

    const teacher = teachers.find(t => t.id === paymentForm.teacherId);
    if (!teacher?.salaryStructure) {
      alert('This teacher does not have a salary structure assigned!');
      return;
    }

    setProcessing(true);
    try {
      const paymentId = `${paymentForm.teacherId}_${paymentForm.month}_${paymentForm.year}`.toLowerCase();

      // Check if payment already exists
      const existingPayment = payments.find(p => p.id === paymentId);
      if (existingPayment && existingPayment.status === 'paid') {
        if (!confirm('Payment for this month already exists and is marked as paid. Do you want to regenerate it?')) {
          setProcessing(false);
          return;
        }
      }

      const totalEarnings = (
        teacher.salaryStructure.basicSalary +
        teacher.salaryStructure.houseRentAllowance +
        teacher.salaryStructure.dearnessAllowance +
        teacher.salaryStructure.medicalAllowance +
        teacher.salaryStructure.transportAllowance +
        teacher.salaryStructure.specialAllowance
      );

      const totalDeductions = (
        teacher.salaryStructure.providentFund +
        teacher.salaryStructure.professionalTax +
        teacher.salaryStructure.incomeTax +
        teacher.salaryStructure.otherDeductions
      );

      const netSalary = totalEarnings - totalDeductions;

      const paymentData: SalaryPayment = {
        id: paymentId,
        teacherId: teacher.id,
        teacherName: teacher.name,
        employeeId: teacher.employeeId || teacher.id,
        month: paymentForm.month,
        year: paymentForm.year,
        basicSalary: teacher.salaryStructure.basicSalary,
        totalEarnings,
        totalDeductions,
        netSalary,
        paymentDate: '',
        paymentMode: paymentForm.paymentMode,
        transactionId: paymentForm.transactionId || '',
        status: 'pending',
        remarks: paymentForm.remarks || '',
        createdAt: new Date().toISOString(),
      };

      await setDoc(doc(db, `school-data/${domain}/salary-payments`, paymentId), paymentData);

      alert(`Payment record generated for ${teacher.name} - ${paymentForm.month} ${paymentForm.year}`);

      // Reset form
      setPaymentForm({
        teacherId: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString(),
        paymentMode: 'Bank Transfer',
        transactionId: '',
        remarks: '',
      });

      loadPayments();
    } catch (error: any) {
      console.error('Error generating payment:', error);
      alert(`Failed to generate payment: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsPaid = async (payment: SalaryPayment) => {
    if (!confirm(`Mark payment as PAID for ${payment.teacherName} - ${payment.month} ${payment.year}?`)) {
      return;
    }

    try {
      const paymentRef = doc(db, `school-data/${domain}/salary-payments`, payment.id);
      await updateDoc(paymentRef, {
        status: 'paid',
        paymentDate: new Date().toISOString().split('T')[0],
        paidAt: new Date().toISOString(),
      });

      alert('Payment marked as paid successfully!');
      loadPayments();
    } catch (error: any) {
      console.error('Error updating payment:', error);
      alert(`Failed to update payment: ${error.message}`);
    }
  };

  // Online Payment via Razorpay
  const handlePayOnline = async (payment: SalaryPayment) => {
    try {
      const teacher = teachers.find(t => t.id === payment.teacherId);
      if (!teacher) {
        alert('Teacher not found!');
        return;
      }

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        alert('Payment gateway is not loaded. Please refresh the page and try again.');
        return;
      }

      // Create Razorpay order
      const options = {
        key: 'rzp_test_YOUR_KEY_ID', // Replace with your Razorpay Key ID
        amount: payment.netSalary * 100, // Amount in paise (multiply by 100)
        currency: 'INR',
        name: 'Smart School',
        description: `Salary Payment - ${payment.month} ${payment.year}`,
        image: '/mb_icon.png', // Your school logo
        order_id: '', // This should be generated from backend in production
        prefill: {
          name: payment.teacherName,
          email: teacher.email || '',
          contact: teacher.phone || '',
        },
        notes: {
          paymentId: payment.id,
          teacherId: payment.teacherId,
          employeeId: payment.employeeId,
          month: payment.month,
          year: payment.year,
          domain: domain,
        },
        theme: {
          color: '#5C2597',
        },
        handler: async function (response: any) {
          // Payment successful
          try {
            const paymentRef = doc(db, `school-data/${domain}/salary-payments`, payment.id);
            await updateDoc(paymentRef, {
              status: 'paid',
              paymentMode: 'Online Payment',
              paymentDate: new Date().toISOString().split('T')[0],
              paidAt: new Date().toISOString(),
              razorpayOrderId: response.razorpay_order_id || '',
              razorpayPaymentId: response.razorpay_payment_id || '',
              razorpaySignature: response.razorpay_signature || '',
              transactionId: response.razorpay_payment_id || '',
            });

            alert(`Payment successful! Payment ID: ${response.razorpay_payment_id}`);
            loadPayments();
          } catch (error: any) {
            console.error('Error updating payment status:', error);
            alert('Payment successful but failed to update status. Please contact support.');
          }
        },
        modal: {
          ondismiss: function () {
            console.log('Payment cancelled by user');
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', async function (response: any) {
        alert(`Payment failed: ${response.error.description}`);
        console.error('Payment failed:', response.error);

        // Update payment status to cancelled
        try {
          const paymentRef = doc(db, `school-data/${domain}/salary-payments`, payment.id);
          await updateDoc(paymentRef, {
            status: 'cancelled',
            remarks: `Payment failed: ${response.error.description}`,
          });
          loadPayments();
        } catch (error) {
          console.error('Error updating failed payment:', error);
        }
      });

      rzp.open();
    } catch (error: any) {
      console.error('Error initiating payment:', error);
      alert(`Failed to initiate payment: ${error.message}`);
    }
  };

  const downloadSalarySlip = (payment: SalaryPayment) => {
    const teacher = teachers.find(t => t.id === payment.teacherId);
    if (!teacher?.salaryStructure) return;

    const slipData = [
      { 'Field': 'Employee Name', 'Value': payment.teacherName },
      { 'Field': 'Employee ID', 'Value': payment.employeeId },
      { 'Field': 'Month', 'Value': `${payment.month} ${payment.year}` },
      { 'Field': 'Payment Date', 'Value': payment.paymentDate || 'Pending' },
      { 'Field': '', 'Value': '' },
      { 'Field': 'EARNINGS', 'Value': '' },
      { 'Field': 'Basic Salary', 'Value': teacher.salaryStructure.basicSalary },
      { 'Field': 'HRA', 'Value': teacher.salaryStructure.houseRentAllowance },
      { 'Field': 'DA', 'Value': teacher.salaryStructure.dearnessAllowance },
      { 'Field': 'Medical Allowance', 'Value': teacher.salaryStructure.medicalAllowance },
      { 'Field': 'Transport Allowance', 'Value': teacher.salaryStructure.transportAllowance },
      { 'Field': 'Special Allowance', 'Value': teacher.salaryStructure.specialAllowance },
      { 'Field': 'Total Earnings', 'Value': payment.totalEarnings },
      { 'Field': '', 'Value': '' },
      { 'Field': 'DEDUCTIONS', 'Value': '' },
      { 'Field': 'Provident Fund', 'Value': teacher.salaryStructure.providentFund },
      { 'Field': 'Professional Tax', 'Value': teacher.salaryStructure.professionalTax },
      { 'Field': 'Income Tax', 'Value': teacher.salaryStructure.incomeTax },
      { 'Field': 'Other Deductions', 'Value': teacher.salaryStructure.otherDeductions },
      { 'Field': 'Total Deductions', 'Value': payment.totalDeductions },
      { 'Field': '', 'Value': '' },
      { 'Field': 'NET SALARY', 'Value': payment.netSalary },
    ];

    const ws = XLSX.utils.json_to_sheet(slipData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salary Slip');

    XLSX.writeFile(wb, `salary_slip_${payment.employeeId}_${payment.month}_${payment.year}.xlsx`);
  };

  const downloadBulkTemplate = () => {
    const templateData = teachers
      .filter(t => t.salaryStructure)
      .map(t => ({
        'Employee ID': t.employeeId || t.id,
        'Employee Name': t.name || '',
        'Month': paymentForm.month,
        'Year': paymentForm.year,
        'Basic Salary': t.salaryStructure!.basicSalary,
        'Total Earnings': (
          t.salaryStructure!.basicSalary +
          t.salaryStructure!.houseRentAllowance +
          t.salaryStructure!.dearnessAllowance +
          t.salaryStructure!.medicalAllowance +
          t.salaryStructure!.transportAllowance +
          t.salaryStructure!.specialAllowance
        ),
        'Total Deductions': (
          t.salaryStructure!.providentFund +
          t.salaryStructure!.professionalTax +
          t.salaryStructure!.incomeTax +
          t.salaryStructure!.otherDeductions
        ),
        'Net Salary': (
          t.salaryStructure!.basicSalary +
          t.salaryStructure!.houseRentAllowance +
          t.salaryStructure!.dearnessAllowance +
          t.salaryStructure!.medicalAllowance +
          t.salaryStructure!.transportAllowance +
          t.salaryStructure!.specialAllowance -
          t.salaryStructure!.providentFund -
          t.salaryStructure!.professionalTax -
          t.salaryStructure!.incomeTax -
          t.salaryStructure!.otherDeductions
        ),
        'Payment Mode': 'Bank Transfer',
        'Transaction ID': '',
        'Remarks': '',
      }));

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bulk Payments');

    XLSX.writeFile(wb, `bulk_salary_${paymentForm.month}_${paymentForm.year}.xlsx`);
  };

  const selectedTeacherData = teachers.find(t => t.id === selectedTeacher);
  const selectedPaymentTeacherData = teachers.find(t => t.id === paymentForm.teacherId);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-cash-coin me-2 text-primary-custom"></i>
          Salary Management
        </h5>
        <small className="text-muted">Assign salary structures and manage payments</small>
      </div>

      <div className="card-body">
        {/* Tab Navigation */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'assign' ? 'active' : ''}`}
              onClick={() => setActiveTab('assign')}
            >
              <i className="bi bi-gear me-2"></i>
              Assign Salary
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              <i className="bi bi-credit-card me-2"></i>
              Payments
            </button>
          </li>
        </ul>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading data...</p>
          </div>
        ) : (
          <>
            {/* Assign Salary Tab */}
            {activeTab === 'assign' && (
              <div>
                <div className="row g-3">
                  {/* Teacher Selection */}
                  <div className="col-md-12">
                    <label className="form-label">
                      <i className="bi bi-person me-2"></i>
                      Select Teacher *
                    </label>
                    <select
                      className="form-select"
                      value={selectedTeacher}
                      onChange={(e) => setSelectedTeacher(e.target.value)}
                    >
                      <option value="">Choose a teacher...</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.employeeId})
                          {teacher.salaryStructure ? ' - Salary Assigned' : ' - No Salary'}
                        </option>
                      ))}
                    </select>
                    {selectedTeacherData && (
                      <small className="text-muted d-block mt-1">
                        Email: {selectedTeacherData.email} | Phone: {selectedTeacherData.phone}
                      </small>
                    )}
                  </div>

                  {selectedTeacher && (
                    <>
                      {/* Effective From */}
                      <div className="col-md-6">
                        <label className="form-label">Effective From *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={salaryForm.effectiveFrom}
                          onChange={(e) => handleSalaryFormChange('effectiveFrom', e.target.value)}
                        />
                      </div>

                      <div className="col-md-12">
                        <hr />
                        <h6 className="text-success">
                          <i className="bi bi-plus-circle me-2"></i>
                          Earnings
                        </h6>
                      </div>

                      {/* Basic Salary */}
                      <div className="col-md-6">
                        <label className="form-label">Basic Salary *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.basicSalary}
                          onChange={(e) => handleSalaryFormChange('basicSalary', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* HRA */}
                      <div className="col-md-6">
                        <label className="form-label">House Rent Allowance (HRA)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.houseRentAllowance}
                          onChange={(e) => handleSalaryFormChange('houseRentAllowance', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* DA */}
                      <div className="col-md-6">
                        <label className="form-label">Dearness Allowance (DA)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.dearnessAllowance}
                          onChange={(e) => handleSalaryFormChange('dearnessAllowance', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Medical Allowance */}
                      <div className="col-md-6">
                        <label className="form-label">Medical Allowance</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.medicalAllowance}
                          onChange={(e) => handleSalaryFormChange('medicalAllowance', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Transport Allowance */}
                      <div className="col-md-6">
                        <label className="form-label">Transport Allowance</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.transportAllowance}
                          onChange={(e) => handleSalaryFormChange('transportAllowance', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Special Allowance */}
                      <div className="col-md-6">
                        <label className="form-label">Special Allowance</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.specialAllowance}
                          onChange={(e) => handleSalaryFormChange('specialAllowance', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      <div className="col-md-12">
                        <hr />
                        <h6 className="text-danger">
                          <i className="bi bi-dash-circle me-2"></i>
                          Deductions
                        </h6>
                      </div>

                      {/* PF */}
                      <div className="col-md-6">
                        <label className="form-label">Provident Fund (PF)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.providentFund}
                          onChange={(e) => handleSalaryFormChange('providentFund', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Professional Tax */}
                      <div className="col-md-6">
                        <label className="form-label">Professional Tax</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.professionalTax}
                          onChange={(e) => handleSalaryFormChange('professionalTax', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Income Tax */}
                      <div className="col-md-6">
                        <label className="form-label">Income Tax (TDS)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.incomeTax}
                          onChange={(e) => handleSalaryFormChange('incomeTax', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Other Deductions */}
                      <div className="col-md-6">
                        <label className="form-label">Other Deductions</label>
                        <input
                          type="number"
                          className="form-control"
                          value={salaryForm.otherDeductions}
                          onChange={(e) => handleSalaryFormChange('otherDeductions', parseFloat(e.target.value) || 0)}
                          min="0"
                        />
                      </div>

                      {/* Summary */}
                      <div className="col-md-12">
                        <hr />
                        <div className="card bg-light">
                          <div className="card-body">
                            <h6 className="mb-3">Salary Summary</h6>
                            <div className="row">
                              <div className="col-md-4">
                                <p className="mb-1">
                                  <strong>Total Earnings:</strong>
                                </p>
                                <h5 className="text-success">₹{calculateTotalEarnings().toLocaleString('en-IN')}</h5>
                              </div>
                              <div className="col-md-4">
                                <p className="mb-1">
                                  <strong>Total Deductions:</strong>
                                </p>
                                <h5 className="text-danger">₹{calculateTotalDeductions().toLocaleString('en-IN')}</h5>
                              </div>
                              <div className="col-md-4">
                                <p className="mb-1">
                                  <strong>Net Salary:</strong>
                                </p>
                                <h5 className="text-primary">₹{calculateNetSalary().toLocaleString('en-IN')}</h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="col-md-12">
                        <button
                          className="btn btn-primary btn-lg w-100"
                          onClick={handleAssignSalary}
                          disabled={processing}
                        >
                          {processing ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              Assign Salary Structure
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div>
                {/* Generate Payment Form */}
                <div className="card mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="card-body">
                    <h6 className="mb-3">
                      <i className="bi bi-plus-square me-2"></i>
                      Generate Payment
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Select Teacher *</label>
                        <select
                          className="form-select"
                          value={paymentForm.teacherId}
                          onChange={(e) => setPaymentForm({ ...paymentForm, teacherId: e.target.value })}
                        >
                          <option value="">Choose a teacher...</option>
                          {teachers
                            .filter(t => t.salaryStructure)
                            .map((teacher) => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.name} ({teacher.employeeId})
                              </option>
                            ))}
                        </select>
                        {selectedPaymentTeacherData?.salaryStructure && (
                          <small className="text-muted d-block mt-1">
                            Net Salary: ₹{(
                              selectedPaymentTeacherData.salaryStructure.basicSalary +
                              selectedPaymentTeacherData.salaryStructure.houseRentAllowance +
                              selectedPaymentTeacherData.salaryStructure.dearnessAllowance +
                              selectedPaymentTeacherData.salaryStructure.medicalAllowance +
                              selectedPaymentTeacherData.salaryStructure.transportAllowance +
                              selectedPaymentTeacherData.salaryStructure.specialAllowance -
                              selectedPaymentTeacherData.salaryStructure.providentFund -
                              selectedPaymentTeacherData.salaryStructure.professionalTax -
                              selectedPaymentTeacherData.salaryStructure.incomeTax -
                              selectedPaymentTeacherData.salaryStructure.otherDeductions
                            ).toLocaleString('en-IN')}
                          </small>
                        )}
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Month *</label>
                        <select
                          className="form-select"
                          value={paymentForm.month}
                          onChange={(e) => setPaymentForm({ ...paymentForm, month: e.target.value })}
                        >
                          {months.map(month => (
                            <option key={month} value={month}>{month}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Year *</label>
                        <select
                          className="form-select"
                          value={paymentForm.year}
                          onChange={(e) => setPaymentForm({ ...paymentForm, year: e.target.value })}
                        >
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Payment Mode</label>
                        <select
                          className="form-select"
                          value={paymentForm.paymentMode}
                          onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                        >
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cash">Cash</option>
                          <option value="Cheque">Cheque</option>
                          <option value="UPI">UPI</option>
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Transaction ID / Reference</label>
                        <input
                          type="text"
                          className="form-control"
                          value={paymentForm.transactionId}
                          onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                          placeholder="Optional"
                        />
                      </div>

                      <div className="col-md-12">
                        <label className="form-label">Remarks</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={paymentForm.remarks}
                          onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                          placeholder="Optional notes"
                        />
                      </div>

                      <div className="col-md-12">
                        <button
                          className="btn btn-primary me-2"
                          onClick={handleGeneratePayment}
                          disabled={processing}
                        >
                          {processing ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus-circle me-2"></i>
                              Generate Payment
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-success"
                          onClick={downloadBulkTemplate}
                        >
                          <i className="bi bi-download me-2"></i>
                          Download Bulk Template
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payments List */}
                <h6 className="mb-3">Payment History</h6>
                {payments.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox display-1 text-muted"></i>
                    <p className="mt-3 text-muted">No payments generated yet.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Period</th>
                          <th>Net Salary</th>
                          <th>Payment Mode</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td>
                              <strong>{payment.teacherName}</strong>
                              <br />
                              <small className="text-muted">{payment.employeeId}</small>
                            </td>
                            <td>{payment.month} {payment.year}</td>
                            <td>
                              <strong>₹{(payment.netSalary || 0).toLocaleString('en-IN')}</strong>
                              <br />
                              <small className="text-muted">
                                Earnings: ₹{(payment.totalEarnings || 0).toLocaleString('en-IN')}
                                <br />
                                Deductions: ₹{(payment.totalDeductions || 0).toLocaleString('en-IN')}
                              </small>
                            </td>
                            <td>{payment.paymentMode}</td>
                            <td>
                              {payment.status === 'paid' ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Paid
                                  {payment.paymentDate && (
                                    <small className="d-block mt-1">{payment.paymentDate}</small>
                                  )}
                                </span>
                              ) : payment.status === 'processing' ? (
                                <span className="badge bg-info">
                                  <i className="bi bi-hourglass-split me-1"></i>
                                  Processing
                                </span>
                              ) : payment.status === 'pending' ? (
                                <span className="badge bg-warning">
                                  <i className="bi bi-clock me-1"></i>
                                  Pending
                                </span>
                              ) : (
                                <span className="badge bg-danger">
                                  <i className="bi bi-x-circle me-1"></i>
                                  Cancelled
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                {payment.status === 'pending' && (
                                  <>
                                    <button
                                      className="btn btn-primary"
                                      onClick={() => handlePayOnline(payment)}
                                      title="Pay Online"
                                    >
                                      <i className="bi bi-credit-card me-1"></i>
                                      Pay
                                    </button>
                                    <button
                                      className="btn btn-success"
                                      onClick={() => handleMarkAsPaid(payment)}
                                      title="Mark as Paid (Manual)"
                                    >
                                      <i className="bi bi-check-lg"></i>
                                    </button>
                                  </>
                                )}
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => downloadSalarySlip(payment)}
                                  title="Download Slip"
                                >
                                  <i className="bi bi-download"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default SalaryManagement;
